import { Button, Col, Row, SegmentControl, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Header } from 'screens/Admin/components';
import TeamPicker from 'screens/Admin/screens/QuickQuiz/components/TeamPicker';
import ChannelConfigSection from './components/ChannelConfigSection';
import CreateWaveForm from './components/CreateWaveForm';
import GlobalDefaultsSection from './components/GlobalDefaultsSection';
import ParticipantsPanel from './components/ParticipantsPanel';
import TimelineSection from './components/TimelineSection';
import WaveDetailPanel from './components/WaveDetailPanel';
import WaveListSection from './components/WaveListSection';

// FanWave admin (P4P Admin) — four tabs:
//   Timeline         — the schedule editor: every wave on one axis, at match or calendar zoom.
//   Waves            — every Game Day / Non-Game Day wave, and the legacy create form.
//   Global Defaults  — platform-wide target, post-FT window and reward rules.
//   Channel Overrides — per-channel target override + Team Admin lock (Section 9 endpoints).
//
// TIMELINE LEADS, and it is where a Game Day wave should now be scheduled: the four loose schedule
// fields (startMinute / endMinute / startsAt / endsAt) and their hand-enforced combinations have
// been replaced by two independently chosen ANCHORS, and only the timeline editor speaks that
// grammar. The Waves tab is kept for what the timeline does not cover — Non-Game Day waves (which
// are their own startsAt/endsAt window and have no anchors), participants, cancel/delete, and the
// archive of runs that have already finished.
const TABS = ['Timeline', 'Waves', 'Global Defaults', 'Channel Overrides'];
const TAB_PARAMS = ['timeline', 'waves', 'defaults', 'channels'];

const FanWave = () => {
  const user = useSelector((state) => state.user);
  const isP4P = !!user?.admin;

  // The whole view rides in the URL so it's shareable and survives refresh (same pattern
  // as the Quick Quiz screen): ?tab=, ?team= (one selected channel shared by the list
  // filter, the create form and the overrides tab), ?mode/status/page (list filters, owned
  // by WaveListSection), ?create=1 (open create panel), ?wave=<key> (open participants).
  const [searchParams, setSearchParams] = useSearchParams();
  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const tabIdx = Math.max(0, TAB_PARAMS.indexOf(searchParams.get('tab') || 'timeline'));
  const setTabIdx = (i) => updateParams({ tab: TAB_PARAMS[i] || TAB_PARAMS[0] });

  // Selected channel (?team=<id>), rehydrated to the full user object on deep-link/refresh.
  const [team, setTeam] = useState(null);
  const teamIdParam = searchParams.get('team');
  useEffect(() => {
    if (!teamIdParam) {
      setTeam(null);
      return;
    }
    if (team?._id === teamIdParam) return;
    let cancelled = false;
    (async () => {
      const res = await Api.post('/admin/getUser', { userId: teamIdParam }, null, {
        silent: true
      });
      if (!cancelled && res?._id) setTeam(res);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamIdParam]);

  const handleTeamChange = (t) => {
    setTeam(t);
    // Reset page in the same URL update — a second setSearchParams call from the list
    // would start from the same stale params and clobber this team write.
    updateParams({ team: t?._id, page: undefined });
  };

  // Create panel open state is URL-driven (?create=1). The same panel edits an existing wave
  // when `editWave` holds a list row — the form prefills from it and PUTs /update with just the
  // changed fields. The row itself only lives in state: a deep-linked ?create=1 always opens a
  // blank create form.
  const [editWave, setEditWave] = useState(null);
  const createOpen = searchParams.get('create') === '1';
  const openCreate = () => {
    setEditWave(null);
    updateParams({ create: '1' });
  };
  const closeCreate = () => {
    setEditWave(null);
    updateParams({ create: undefined });
  };

  const openEdit = useCallback(
    (wave) => {
      // The form is per-channel (config, fixtures), so editing selects the wave's channel too.
      if (wave.teamId && wave.teamId !== teamIdParam) {
        setTeam({ _id: wave.teamId, ...(wave.team || {}) });
        updateParams({ team: wave.teamId, create: '1', page: undefined });
      } else {
        updateParams({ create: '1' });
      }
      setEditWave(wave);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamIdParam, updateParams]
  );

  // Participants drill-down: the panel needs the full list row for its header, so the row
  // lives in state while ?wave=<waveId> carries the key. On deep-link/refresh the list
  // rehydrates it once the matching row loads (see pendingParticipantsKey below). The waveId
  // leads: a match can hold several waves, and its gameId is shared by all of them.
  const [participantsWave, setParticipantsWave] = useState(null);
  const openParticipants = useCallback(
    (wave) => {
      setParticipantsWave(wave);
      setDetailWave(null);
      updateParams({ wave: wave.waveId || wave.gameId, detail: undefined });
    },
    [updateParams]
  );
  const closeParticipants = () => {
    setParticipantsWave(null);
    updateParams({ wave: undefined });
  };
  const pendingParticipantsKey = participantsWave ? null : searchParams.get('wave');

  // Config drill-down (?detail=<waveId>): clicking a wave row opens a read-only panel of that
  // wave's configuration. Mirrors the participants panel above; the two are mutually exclusive
  // so only one side panel is ever open.
  const [detailWave, setDetailWave] = useState(null);
  const openDetail = useCallback(
    (wave) => {
      setDetailWave(wave);
      setParticipantsWave(null);
      updateParams({ detail: wave.waveId || wave.gameId, wave: undefined });
    },
    [updateParams]
  );
  const closeDetail = () => {
    setDetailWave(null);
    updateParams({ detail: undefined });
  };
  const pendingDetailKey = detailWave ? null : searchParams.get('detail');

  // Bumped after each successful create so the list refetches.
  const [refreshToken, setRefreshToken] = useState(0);

  // An update echoes the wave's resulting state, so the list patches that one row instead of
  // refetching. The counter keeps repeated saves of the same wave distinct.
  const [rowPatch, setRowPatch] = useState(null);
  const patchRow = useCallback((wave) => {
    setRowPatch((prev) => ({ nonce: (prev?.nonce || 0) + 1, wave }));
  }, []);

  // A 409 in the edit panel means the wave is spent — hand it to the list's delete dialog.
  const [deleteRequest, setDeleteRequest] = useState(null);
  const requestDelete = useCallback(
    (wave) => {
      closeCreate();
      setDeleteRequest((prev) => ({ nonce: (prev?.nonce || 0) + 1, wave }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateParams]
  );

  if (!isP4P) {
    return (
      <Col centerAll ht="60vh">
        <Text h3>Not authorized</Text>
        <Text col={theme.GREY_60} marg="8px 0 0">
          FanWave management is available to P4P admins only.
        </Text>
      </Col>
    );
  }

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>FanWave</Text>
        </Row>
      </Header>

      <Col start noFlex marg="54px 0 0" wid="100%" pad="24px 24px 48px" gap="20px">
        <Row center between noFlex wid="100%" gap="14px" style={{ flexWrap: 'wrap' }}>
          <SegmentControl
            options={TABS}
            selectedIdx={tabIdx}
            handleSelect={setTabIdx}
            small
            wid="680px"
            textStyle={{ size: '14px' }}
          />
          {tabIdx === 1 && (
            <Button
              text="+ Create FanWave"
              onClick={openCreate}
              bg={theme.SECONDARY}
              small
              wid="170px"
            />
          )}
        </Row>

        {tabIdx === 0 && <TimelineSection team={team} onTeamChange={handleTeamChange} />}

        {tabIdx === 1 && (
          <WaveListSection
            team={team}
            onTeamChange={handleTeamChange}
            refreshToken={refreshToken}
            onOpenParticipants={openParticipants}
            pendingParticipantsKey={pendingParticipantsKey}
            onOpenDetail={openDetail}
            pendingDetailKey={pendingDetailKey}
            onEdit={openEdit}
            onCreate={openCreate}
            rowPatch={rowPatch}
            deleteRequest={deleteRequest}
          />
        )}

        {tabIdx === 2 && <GlobalDefaultsSection />}

        {tabIdx === 3 && (
          <>
            <Col
              noFlex
              wid="100%"
              pad="20px 22px"
              gap="14px"
              style={{
                backgroundColor: '#fff',
                borderRadius: '14px',
                border: `1px solid ${theme.GREY_20}`,
                boxShadow: '0 1px 2px rgba(20, 28, 40, 0.04)'
              }}
            >
              <Col noFlex wid="100%" gap="2px">
                <Text h4 bold marg="0">
                  Per-Channel
                </Text>
                <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12.5px' }}>
                  Pick a channel to set its tap target or lock it for the Team Admin.
                </Text>
              </Col>
              <Col noFlex wid="100%" style={{ maxWidth: '460px' }}>
                <TeamPicker team={team} onChange={handleTeamChange} label={null} />
              </Col>
            </Col>

            {team ? (
              <ChannelConfigSection team={team} />
            ) : (
              <Col
                centerAll
                noFlex
                wid="100%"
                pad="40px 0"
                gap="4px"
                style={{
                  borderRadius: '14px',
                  border: `1px dashed ${theme.GREY_30}`,
                  background: theme.GREY_10
                }}
              >
                <Text h4 col={theme.GREY_70} marg="0">
                  No channel selected
                </Text>
                <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12.5px' }}>
                  Pick a channel above to manage its FanWave target.
                </Text>
              </Col>
            )}
          </>
        )}
      </Col>

      {/* Panels stay mounted so the Sidepanel slide animation runs on open and close. */}
      <CreateWaveForm
        visible={createOpen}
        team={team}
        onTeamChange={handleTeamChange}
        onClose={closeCreate}
        onCreated={() => setRefreshToken((t) => t + 1)}
        editWave={editWave}
        onUpdated={patchRow}
        onDeleteInstead={requestDelete}
      />

      <ParticipantsPanel
        visible={!!participantsWave}
        wave={participantsWave}
        onClose={closeParticipants}
      />

      <WaveDetailPanel visible={!!detailWave} wave={detailWave} onClose={closeDetail} />
    </Col>
  );
};

export default FanWave;
